import React from 'react';
import { ArrowRight, Check, Minus } from 'lucide-react';
import BlurFade from '../primitives/BlurFade';
import { Section } from '../layout/Section';
import { Container } from '../layout/Container';
import { Eyebrow, Headline, Body } from '../layout/typography';
import { useLang } from '../../LangContext';
import { mediaAlt } from '../../lib/mediaSeo';

/**
 * TypeCompareBlock — side-by-side product-type comparison.
 *
 * Two large cards: e.g. Spannrahmen vs. Drehfenster. Each card has its own
 * photo, badge, name, tagline, "voor wie?" copy, and a pros/cons list. A
 * single CTA at the bottom invites the user to ask for advice if they're
 * unsure which type fits their situation.
 *
 * Inspired by clip-shade.de's deeper product comparison narratives — sells
 * by educating rather than pushing a single SKU.
 *
 * Props: { id, testId, tone, rhythm, eyebrow, title, sub, items, ctaLabel,
 *          ctaHref, ctaHint, ctaTestId, ctaSource }
 */
export default function TypeCompareBlock({
  id, testId = 'type-compare', tone = 'cream', rhythm = 'grand',
  eyebrow, title, sub,
  items = [],
  forWhoLabel = 'Voor wie?',
  ctaLabel, ctaHref = '#cta-block', ctaHint, ctaTestId = 'type-compare-cta', ctaSource = 'type-compare',
}) {
  const { lang, overlay } = useLang();
  return (
    <Section id={id} data-testid={testId} tone={tone} rhythm={rhythm}>
      <Container>
        <div className="max-w-3xl mb-12 lg:mb-16">
          {eyebrow && <BlurFade><Eyebrow className="mb-7">{eyebrow}</Eyebrow></BlurFade>}
          {title && <BlurFade delay={0.05}><Headline size="lg">{title}</Headline></BlurFade>}
          {sub && (
            <BlurFade delay={0.12}>
              <Body className="mt-6 text-[15.5px] lg:text-[17px] leading-relaxed max-w-2xl">{sub}</Body>
            </BlurFade>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {items.map((p, i) => (
            <BlurFade key={p.key} delay={i * 0.1} offset={20}>
              <article
                data-testid={`type-${p.key}`}
                className="group h-full flex flex-col rounded-[22px] overflow-hidden bg-white border border-[#E5E0D2] transition-all duration-500 hover:border-[#F26B1A]/40 hover:shadow-[0_30px_60px_-30px_rgba(242,107,26,0.30)] hover:-translate-y-1"
              >
                {/* Photo */}
                <div className="relative aspect-[16/10] overflow-hidden bg-[#F2EEDF]">
                  <img
                    src={p.image}
                    alt={mediaAlt(overlay, p.image, lang, p.name || '')}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-[1.05]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/15 to-transparent" />
                  {p.badge && (
                    <span className="absolute top-4 left-4 inline-flex items-center px-2.5 py-1 rounded-full bg-[#F26B1A] text-white text-[10.5px] uppercase tracking-[0.15em] font-semibold shadow-[0_4px_14px_-4px_rgba(242,107,26,0.55)]">
                      {p.badge}
                    </span>
                  )}
                  {p.tag && (
                    <span className="absolute bottom-4 left-4 inline-flex items-center px-2.5 py-1 rounded-full bg-white/90 backdrop-blur-md border border-black/5 text-[#1F1D1A] text-[11px] tracking-wide">
                      {p.tag}
                    </span>
                  )}
                </div>

                {/* Body */}
                <div className="flex flex-col flex-1 p-6 lg:p-9">
                  <h3 className="font-display text-[#1F1D1A] text-[28px] lg:text-[34px] leading-tight">
                    {p.name}
                  </h3>
                  {p.tagline && (
                    <p className="mt-2 font-display text-[#F26B1A] text-[17px] lg:text-[19px] leading-snug font-light">
                      {p.tagline}
                    </p>
                  )}
                  {p.desc && (
                    <Body className="mt-5 text-[14.5px] leading-relaxed">{p.desc}</Body>
                  )}

                  {p.forWho && (
                    <div className="mt-6 p-4 rounded-xl bg-[#FAF4E6] border border-[#F2DFB3] text-[13.5px] leading-relaxed text-[#1F1D1A]">
                      <span className="block text-[10.5px] uppercase tracking-[0.18em] text-[#A67A14] font-semibold mb-1.5">
                        {forWhoLabel}
                      </span>
                      {p.forWho}
                    </div>
                  )}

                  {(p.pros?.length || p.cons?.length) && (
                    <ul className="mt-6 space-y-2.5">
                      {p.pros?.map((pro, k) => (
                        <li key={`pro-${k}`} className="flex items-start gap-2.5 text-[13.5px] text-[#1F1D1A]">
                          <span className="mt-0.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#F26B1A] shrink-0">
                            <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                          </span>
                          <span className="leading-snug">{pro}</span>
                        </li>
                      ))}
                      {p.cons?.map((con, k) => (
                        <li key={`con-${k}`} className="flex items-start gap-2.5 text-[13.5px] text-[#5C5852]">
                          <span className="mt-0.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#E5E0D2] shrink-0">
                            <Minus className="w-2.5 h-2.5 text-[#5C5852]" strokeWidth={3} />
                          </span>
                          <span className="leading-snug">{con}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </article>
            </BlurFade>
          ))}
        </div>

        {/* Single CTA at the bottom */}
        {ctaLabel && (
          <BlurFade delay={0.25}>
            <div className="mt-12 lg:mt-16 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <a
                href={ctaHref}
                data-testid={ctaTestId}
                data-lead-source={ctaSource}
                className="pill pill-leaf"
              >
                {ctaLabel} <ArrowRight className="w-4 h-4" />
              </a>
              {ctaHint && (
                <span className="text-[#5C5852] text-[13px]">{ctaHint}</span>
              )}
            </div>
          </BlurFade>
        )}
      </Container>
    </Section>
  );
}
